import {
  FILL_BLANK_MARKER,
  fillBlankStemForDelivery,
  isFillBlankRichHtml,
} from "@/components/question-composer/fill-blank-html"
import { leadInPlainText } from "@/lib/lead-in-html"
import type { AuthoringQuestionType } from "@/lib/library-authoring"

import { sortQuestionQualityChecks } from "./confidence"
import { formatTypeAvailable } from "./format-ask"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualitySnapshot,
} from "./types"
import type { VariationTarget } from "./variation-ask"

export const SELECTION_ENHANCE_CHECK_ID = "selection-enhance"

export type ComposerEnhanceSelection = {
  field: "leadIn" | "option"
  optionId?: string
  optionLetter?: string
  text: string
}

function normalizeNeedle(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase()
}

function textsOverlap(left: string, right: string): boolean {
  const a = normalizeNeedle(left)
  const b = normalizeNeedle(right)
  if (!a || !b) return false
  return a.includes(b) || b.includes(a)
}

export function selectionAskHomeLabel(selection: ComposerEnhanceSelection): string {
  if (selection.field === "leadIn") return "Question"
  return selection.optionLetter ? `Option ${selection.optionLetter}` : "Option"
}

/** Authoring label in variation copy — stem, distractor A. */
export function selectionVariationFieldLabel(
  selection: ComposerEnhanceSelection,
): string {
  if (selection.field === "leadIn") return "stem"
  return selection.optionLetter
    ? `distractor ${selection.optionLetter}`
    : "distractor"
}

export function selectionCoversField(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
): boolean {
  if (selection.field === "leadIn") {
    const full = toSelectionPlain(snapshot.leadIn)
    return normalizeNeedle(full) === normalizeNeedle(selection.text)
  }
  const optionId = optionIdForSelection(selection, snapshot.options)
  const option = snapshot.options.find((row) => row.id === optionId)
  if (!option) return false
  return (
    normalizeNeedle(toSelectionPlain(option.text)) ===
    normalizeNeedle(selection.text)
  )
}

/** Best variation scope from what the author selected on the canvas. */
export function selectionVariationTarget(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
): VariationTarget {
  if (selection.field === "leadIn") return "stem"
  if (selectionCoversField(snapshot, selection)) return "options-one"
  return "options-one"
}

/** Canvas selection on a choice row — variation intent, not the quality trail. */
export function isChoiceVariationSelection(
  selection: ComposerEnhanceSelection,
): boolean {
  return selection.field === "option"
}

export function selectionAskPrompt(
  selection: ComposerEnhanceSelection,
  options: { hasQualityOverlap?: boolean } = {},
): string {
  const text = selection.text.trim().replace(/"/g, "'")
  if (options.hasQualityOverlap) {
    return `Enhance this selection on ${selectionAskHomeLabel(selection)}: "${text}"`
  }
  return `Vary this selection on ${selectionVariationFieldLabel(selection)}: "${text}"`
}

/** Variation intent needs at least two wordings on a single selected span. */
export function ensureMinSpanRewrites(
  span: string,
  rewrites: string[],
  min = 2,
): string[] {
  const out = uniqueRewrites(span, rewrites)
  const text = toSelectionPlain(span)
  const words = text.split(/\s+/).filter(Boolean)
  if (out.length >= min || !text) return out.slice(0, Math.max(min, out.length))

  const bare = text.replace(/^the\s+/i, "").trim()
  if (bare && !/^the\s/i.test(text)) {
    const theForm = `The ${bare.charAt(0).toLowerCase()}${bare.slice(1)}`
    if (!out.some((row) => row.toLowerCase() === theForm.toLowerCase())) {
      out.push(theForm)
    }
  }
  if (out.length < min && bare) {
    const inForm = `In the ${bare.toLowerCase()}`
    if (!out.some((row) => row.toLowerCase() === inForm.toLowerCase())) {
      out.push(sentenceCase(inForm))
    }
  }
  if (out.length < min && words.length === 1 && bare) {
    const named = `${sentenceCase(bare)} compartment`
    if (!out.some((row) => row.toLowerCase() === named.toLowerCase())) {
      out.push(named)
    }
  }
  return out.slice(0, Math.max(min, out.length))
}

export function parseSelectionAsk(prompt: string): ComposerEnhanceSelection | null {
  const trimmed = prompt.trim()
  const vary = trimmed.match(
    /^Vary this selection on (stem|distractor(?: ([A-H0-9]+))?):\s*"([\s\S]+)"\s*$/i,
  )
  if (vary) {
    const text = vary[3]?.trim() ?? ""
    if (!text) return null
    if (/^stem$/i.test(vary[1] ?? "")) {
      return { field: "leadIn", text }
    }
    return {
      field: "option",
      optionLetter: vary[2],
      text,
    }
  }
  const enhance = trimmed.match(
    /^Enhance this selection on (Question|Option ([A-H0-9]+)):\s*"([\s\S]+)"\s*$/i,
  )
  if (!enhance) return null
  const text = enhance[3]?.trim() ?? ""
  if (!text) return null
  if (/^question$/i.test(enhance[1] ?? "")) {
    return { field: "leadIn", text }
  }
  return {
    field: "option",
    optionLetter: enhance[2],
    text,
  }
}

export function selectionOverlapsCheck(
  selection: ComposerEnhanceSelection,
  check: QuestionQualityCheck,
): boolean {
  if (selection.field === "leadIn") {
    if (check.field !== "leadIn") return false
  } else if (check.field === "option") {
    if (
      check.optionId &&
      selection.optionId &&
      check.optionId !== selection.optionId
    ) {
      return false
    }
  } else if (check.field !== "options") {
    return false
  }

  if (!check.markText) return false
  const mark = normalizeNeedle(check.markText)
  const selected = normalizeNeedle(selection.text)
  if (!mark || !selected) return false
  const current = check.currentText ? normalizeNeedle(check.currentText) : ""
  if (current && mark === current) {
    return selected === mark || selected.includes(mark)
  }
  return mark.includes(selected) || selected.includes(mark)
}

export function optionIdForSelection(
  selection: ComposerEnhanceSelection,
  options: Array<{ id: string }>,
): string | undefined {
  if (selection.optionId) return selection.optionId
  const letter = selection.optionLetter?.trim().toUpperCase()
  if (!letter) return undefined
  const index = letter.charCodeAt(0) - 65
  if (index < 0 || index >= options.length) return undefined
  return options[index]?.id
}

export function qualityCheckForSelection(
  checks: QuestionQualityCheck[],
  selection: ComposerEnhanceSelection,
): QuestionQualityCheck | null {
  const overlapping = checks.filter((check) =>
    selectionOverlapsCheck(selection, check),
  )
  return sortQuestionQualityChecks(overlapping)[0] ?? null
}

export function toSelectionPlain(value: string): string {
  const fromHtml = isFillBlankRichHtml(value)
    ? fillBlankStemForDelivery(value)
    : leadInPlainText(value) || value
  return fromHtml
    .replace(/Blank\s+\d+/gi, FILL_BLANK_MARKER)
    .replace(/_{2,}/g, FILL_BLANK_MARKER)
    .replace(/\s+/g, " ")
    .trim()
}

export function replaceSelectedSpan(
  source: string,
  selected: string,
  nextSpan: string,
): string {
  const haystack = toSelectionPlain(source)
  const needle = toSelectionPlain(selected)
  const next = toSelectionPlain(nextSpan)
  if (!needle) return haystack
  const idx = haystack.toLowerCase().indexOf(needle.toLowerCase())
  if (idx < 0) return haystack
  return `${haystack.slice(0, idx)}${next}${haystack.slice(idx + needle.length)}`
}

function sentenceCase(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return trimmed
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
}

function uniqueRewrites(span: string, candidates: string[]): string[] {
  const current = toSelectionPlain(span).toLowerCase()
  const seen = new Set<string>([current])
  const out: string[] = []
  for (const candidate of candidates) {
    const next = toSelectionPlain(candidate)
    const key = next.toLowerCase()
    if (!next || seen.has(key)) continue
    seen.add(key)
    out.push(next)
  }
  return out
}

/** Conservative item-writing moves on the selected span. No clinical synonyms. */
export function selectionSpanRewrites(span: string): string[] {
  const text = toSelectionPlain(span).replace(/[.?!]+$/, "").trim()
  if (!text) return []
  const candidates: string[] = []

  const provides = text.match(
    /^(The\s+.+?)\s+provides\s+(.+?)\s+to\s+(.+)$/i,
  )
  if (provides) {
    const subject = provides[1].trim()
    const gift = provides[2].trim()
    const target = provides[3].trim()
    candidates.push(
      `${sentenceCase(gift)} to ${target} is provided by ${subject.replace(/^The\s/i, "the ")}.`,
    )
    candidates.push(
      `${sentenceCase(target)} receive ${gift} from ${subject.replace(/^The\s/i, "the ")}.`,
    )
    if (!/\bwhich\b/i.test(text)) {
      candidates.push(
        `Which ${subject.replace(/^The\s+/i, "")} provides ${gift} to ${target}?`,
      )
    }
  }

  const providedBy = text.match(/^(.+?)\s+is provided by\s+(.+)$/i)
  if (providedBy) {
    const gift = providedBy[1].trim()
    const subject = providedBy[2].trim()
    candidates.push(`${sentenceCase(subject)} provides ${gift.replace(/^[A-Z]/, (ch) => ch.toLowerCase())}.`)
  }

  const isArticle = text.match(/^(The\s+.+?)\s+is\s+(an|a|the)$/i)
  if (isArticle) {
    const subject = isArticle[1].trim()
    const asThe = subject.replace(/^The\s/i, "the ")
    candidates.push(`${subject} is?`)
    candidates.push(`What is ${asThe}?`)
    candidates.push(`Name ${asThe}.`)
  }

  const which = text.match(/^Which\s+(.+)$/i)
  if (which) {
    const rest = which[1].trim()
    candidates.push(`What ${rest}?`)
    candidates.push(`Identify which ${rest}.`)
  }

  const howDo = text.match(/^How do\s+(.+)$/i)
  if (howDo) {
    const rest = howDo[1].trim()
    candidates.push(`In what way do ${rest}?`)
    candidates.push(`Describe how ${rest}.`)
  }

  if (candidates.length === 0 && text.split(/\s+/).length <= 4) {
    if (/^the\s/i.test(text)) {
      candidates.push(sentenceCase(text.replace(/^the\s+/i, "")))
    } else {
      candidates.push(`The ${text.charAt(0).toLowerCase()}${text.slice(1)}`)
    }
  }

  if (candidates.length === 0 && !/[?]/.test(span) && text.split(" ").length >= 5) {
    candidates.push(`${text}?`)
    if (/^The\s/i.test(text)) {
      candidates.push(`What is ${text.replace(/^The\s/i, "the ").replace(/\s+is$/i, "")}?`)
    }
  }

  return uniqueRewrites(span, candidates)
}

function selectionSource(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
): string {
  if (selection.field === "option") {
    const optionId = optionIdForSelection(selection, snapshot.options)
    return (
      snapshot.options.find((option) => option.id === optionId)?.text ??
      selection.text
    )
  }
  return snapshot.leadIn || selection.text
}

export function selectionEnhancementCheck(
  snapshot: QuestionQualitySnapshot,
  selection: ComposerEnhanceSelection,
): QuestionQualityCheck | null {
  const source = selectionSource(snapshot, selection)
  const span = toSelectionPlain(selection.text)
  const rewrites = ensureMinSpanRewrites(span, selectionSpanRewrites(span))
  if (rewrites.length === 0) return null

  const home = selectionAskHomeLabel(selection)
  const optionId = optionIdForSelection(selection, snapshot.options)
  const parameterDeltas = [
    { parameter: "clarity" as const, effect: "clears" as const },
    { parameter: "cognitive" as const, effect: "locked" as const },
  ]
  const alternates: QuestionQualityAlternate[] = rewrites.map((rewrite, index) => {
    const nextField = replaceSelectedSpan(source, span, rewrite)
    return {
      id: `selection-wording-${index}`,
      text: nextField.replaceAll(FILL_BLANK_MARKER, "______"),
      actionKind: "apply-text",
      actionLabel: "Use this wording",
      patch:
        selection.field === "option"
          ? { optionText: nextField }
          : { leadIn: nextField },
      parameterDeltas,
      clearsCheckIds: [SELECTION_ENHANCE_CHECK_ID],
    }
  })

  return {
    id: SELECTION_ENHANCE_CHECK_ID,
    parameter: "clarity",
    severity: "caution",
    field: selection.field === "option" ? "option" : "leadIn",
    optionId,
    title: `Enhance this selection on ${home}`,
    mechanism: "Replace the selected span. Use this writes it on the canvas.",
    currentText: toSelectionPlain(source),
    rubricConfidence: "medium",
    actionKind: "apply-text",
    actionLabel: "Use this wording",
    patch: alternates[0]?.patch,
    alternates,
  }
}

export function selectionAskFollowUps(
  snapshot: QuestionQualitySnapshot,
  capabilities: {
    numeric: boolean
    fillBlank: boolean
    matchingPairs: boolean
    ordering: boolean
    hotspot: boolean
  },
  options: { hasWording: boolean },
): string[] {
  const rows: string[] = []
  if (options.hasWording) rows.push("Rewrite the stem")
  const current = snapshot.type
  const candidates: Array<{ type: AuthoringQuestionType; label: string }> = [
    { type: "fill_blank", label: "Change to Fill in the blank" },
    { type: "mcq_multiple", label: "Change to Multi-select" },
    { type: "mcq_single", label: "Change to Single choice" },
  ]
  for (const row of candidates) {
    if (row.type === current) continue
    if (!formatTypeAvailable(row.type, capabilities)) continue
    rows.push(row.label)
    if (rows.length >= 3) break
  }
  return rows
}
